import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { B3SelectItem } from 'src/app/shared/models/b3-select-item.model';
import { Subscription } from 'rxjs';
import {
  FORM_REGISTER,
  WIZARD_STEP_NAV,
  WizardStepNavigation,
} from 'src/app/pages/projects-repository/register/project/project-register.component';
import { ProjectBaselineActivityService } from 'src/app/pages/projects-repository/services/project-baseline-activity.service';
import { ProjectMethodologiesService } from 'src/app/pages/projects-repository/services/project-methodology.service';
import { Developer, Locations, PsaProjectData } from 'src/app/shared/interfaces/psa-project-register';
import { formatCPFOrCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';
import { ODS_ITEMS } from 'src/app/shared/constants/ods-items.constant';
import {
  ConfirmationOdsSummary,
  ConfirmationStepSummary,
} from './project-register-confirmation.types';

@Component({
  standalone: false,
  selector: 'app-psa-step-confirmation',
  templateUrl: './step-confirmation.component.html',
  styleUrls: ['./step-confirmation.component.scss'],
})
export class PsaStepConfirmationComponent implements OnInit, OnDestroy {
  stepSummaries: ConfirmationStepSummary[] = [];
  selectedOds: ConfirmationOdsSummary[] = [];
  coBenefitsSummary = '-';
  odsCountLabel = '-';

  private selectBaselineActivity: B3SelectItem[] = [];
  private selectMethodology: B3SelectItem[] = [];
  private formChangesSub?: Subscription;

  constructor(
    @Inject(FORM_REGISTER) public formRegister: FormGroup,
    @Inject('projectDraftData') public projectDraftData: PsaProjectData | undefined,
    @Inject(WIZARD_STEP_NAV) private wizardNav: WizardStepNavigation,
    private projectBaselineActivityService: ProjectBaselineActivityService,
    private projectMethodologiesService: ProjectMethodologiesService,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    this.loadBaselinesActivity();
    this.loadMethodologies();
    this.buildConfirmationSummary();

    this.formChangesSub = this.formRegister.valueChanges.subscribe(() => {
      this.buildConfirmationSummary();
    });
  }

  ngOnDestroy(): void {
    this.formChangesSub?.unsubscribe();
  }

  editStep(stepIndex: number): void {
    this.wizardNav.goToStep(stepIndex);
  }

  private loadBaselinesActivity(): void {
    this.projectBaselineActivityService.getBaselinesActivity().subscribe(response => {
      this.selectBaselineActivity = (response.data ?? []).map((baselineActivity: any) => ({
        inputId: baselineActivity.baselineActivityIdentifier,
        label: baselineActivity.baselineActivityDescription,
        data: baselineActivity.baselineActivityIdentifier,
      }));
      this.buildConfirmationSummary();
    });
  }

  private loadMethodologies(): void {
    this.projectMethodologiesService.getMethodologies().subscribe(response => {
      this.selectMethodology = (response.data ?? []).map((methodology: any) => ({
        inputId: methodology.methodologyIdentifier,
        label: methodology.methodologyDescription,
        data: methodology.methodologyIdentifier,
      }));
      this.buildConfirmationSummary();
    });
  }

  buildConfirmationSummary(): void {
    const form = this.formRegister.value;
    const data = this.projectDraftData;
    const developers = (form.developers ?? data?.developers ?? []) as Developer[];
    const principal = developers.find(d => d.principal);
    const otherDeveloper = developers.find(d => !d.principal);
    const locations = (form.locations ?? data?.locations ?? []) as Locations[];
    const location = locations[0];
    const attachmentLinks = (form.attachments ?? data?.attachments ?? [])
      .map((item: { link?: string }) => item.link)
      .filter((link: string | undefined): link is string => !!link && link.trim() !== '');

    this.selectedOds = (form.odsNumbers ?? data?.odsNumbers ?? [])
      .map((id: number) => ODS_ITEMS.find(item => item.id === id))
      .filter((item: (typeof ODS_ITEMS)[number] | undefined): item is (typeof ODS_ITEMS)[number] => !!item)
      .map((item: (typeof ODS_ITEMS)[number]) => ({ id: item.id, label: item.label }));

    const odsCount = this.selectedOds.length;
    this.odsCountLabel = odsCount === 1
      ? '1 objetivo selecionado'
      : `${odsCount} objetivos selecionados`;
    this.coBenefitsSummary = this.display(form.coBenefits ?? data?.coBenefits);

    const baselineLabel = this.resolveBaselineLabel(form.baselineActivity, data?.baselineActivityId);
    const methodologyLabel = this.resolveMethodologyLabel(form.methodology, data?.methodologyId);

    this.stepSummaries = [
      {
        stepIndex: 0,
        title: 'Etapa 1 - Dados Gerais',
        meta: [
          { label: 'Conta registrador', value: this.display(form.registerCode ?? data?.registerCode) },
          { label: 'Status', value: this.formatStatus(data?.status) },
        ],
        highlight: this.display(form.projectName ?? data?.projectName),
        rows: [
          [
            {
              label: 'Data de início do projeto',
              value: this.formatDate(form.projectStartDate ?? data?.projectStartDate),
            },
            { label: 'Atividade de linha de base', value: baselineLabel },
            { label: 'Descrição do projeto', value: this.display(form.generalDetails ?? data?.generalDetails) },
          ],
          [
            { label: 'Desenvolvedor principal', value: this.display(principal?.developerName ?? form.developerCompanyName) },
            {
              label: 'CPF/CNPJ',
              value: this.formatDocument(principal?.cpfCnpj ?? form.developerCnpj),
            },
            { label: 'E-mail', value: this.display(principal?.email ?? form.developerEmail) },
          ],
          [
            { label: 'Telefone', value: this.display(principal?.phone ?? form.developerPhone) },
            { label: 'Outro desenvolvedor', value: this.display(otherDeveloper?.developerName) },
            { label: 'CPF/CNPJ', value: this.formatDocument(otherDeveloper?.cpfCnpj) },
          ],
        ],
      },
      {
        stepIndex: 1,
        title: 'Etapa 2 - Propriedades',
        meta: [
          { label: 'CPF/CNPJ', value: this.formatDocument(location?.landOwnerCpfCnpj) },
          { label: 'Município', value: this.display(location?.municipality) },
        ],
        highlight: this.display(location?.landOwnerFullName),
        rows: [
          [
            { label: 'Coordenadas', value: this.formatCoordinates(location) },
            { label: 'País', value: this.display(location?.country) },
            { label: 'UF', value: this.display(location?.state) },
          ],
          [
            { label: 'Hectares Totais', value: this.formatNumber(location?.totalHectares) },
          ],
        ],
      },
      {
        stepIndex: 2,
        title: 'Etapa 3 - Metodologia',
        meta: [
          {
            label: 'Link do relatório',
            value: this.display(form.ramComplianceReportLink ?? data?.methodologyReportLink),
          },
        ],
        highlight: methodologyLabel,
        rows: [
          [
            {
              label: 'Descrição do relatório',
              value: this.display(form.ramComplianceReportDetail ?? data?.methodologyDocumentDescription),
            },
            {
              label: 'Documentos relevantes',
              value: this.display(form.documentDescription ?? data?.otherDocumentationDescription),
            },
          ],
          [
            { label: 'Link dos documentos', value: attachmentLinks.length ? attachmentLinks.join(', ') : '-' },
          ],
        ],
      },
    ];
  }

  private resolveBaselineLabel(formValue: unknown, draftId?: number | null): string {
    if (formValue && typeof formValue === 'object' && 'label' in (formValue as B3SelectItem)) {
      return String((formValue as B3SelectItem).label);
    }
    if (draftId != null) {
      return this.selectBaselineActivity.find(item => item.inputId == draftId)?.label ?? String(draftId);
    }
    return '-';
  }

  private resolveMethodologyLabel(formValue: unknown, draftId?: number | null): string {
    if (formValue && typeof formValue === 'object' && 'label' in (formValue as B3SelectItem)) {
      return String((formValue as B3SelectItem).label);
    }
    if (draftId != null) {
      return this.selectMethodology.find(item => item.inputId == draftId)?.label ?? String(draftId);
    }
    return '-';
  }

  private formatDocument(value?: string | null): string {
    if (!value) {
      return '-';
    }
    return formatCPFOrCNPJ(value);
  }

  private formatCoordinates(location?: Locations): string {
    if (!location?.latitude && !location?.longitude) {
      return '-';
    }
    return `Lat ${this.display(location.latitude)} / Long ${this.display(location.longitude)}`;
  }

  private formatNumber(value?: number | null): string {
    if (value == null || Number.isNaN(value)) {
      return '-';
    }
    return new Intl.NumberFormat('pt-BR').format(value);
  }

  private formatDate(value?: unknown): string {
    if (!value) {
      return '-';
    }

    if (value instanceof Date) {
      return this.datePipe.transform(value, 'dd/MM/yyyy') ?? '-';
    }

    const dateString = String(value).substring(0, 10);
    const [year, month, day] = dateString.split('-').map(Number);
    if (!year || !month || !day) {
      return '-';
    }

    return this.datePipe.transform(new Date(year, month - 1, day), 'dd/MM/yyyy') ?? '-';
  }

  private formatStatus(value?: string | null): string {
    if (!value) {
      return '-';
    }

    if (value === 'PROJECT_DRAFT' || value === 'RASCUNHO') {
      return 'RASCUNHO';
    }

    return String(value).replace(/_/g, ' ');
  }

  private display(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    return String(value);
  }
}
